import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Sparkles, ExternalLink, Star, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSocialMeta } from "@/hooks/useSocialMeta";
import { mediaUrl, goUrl } from "@/lib/api";
import axios from "axios";

const BASE_URL = "https://horseappcompare.com";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const setJsonLd = (id, data) => {
  document.getElementById(id)?.remove();
  const s = document.createElement("script");
  s.id = id;
  s.type = "application/ld+json";
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
};

const AppFeaturePage = () => {
  const { slug, label } = useParams();
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    setStatus("loading");
    axios
      .get(`${API}/apps/${slug}/feature/${label}`)
      .then((r) => { setData(r.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug, label]);

  const featureName = data?.main?.feature_label || "";
  const appName = data?.app?.name || "";
  const ogImg = data ? `${process.env.REACT_APP_BACKEND_URL}/api/og/feature/${slug}/${label}.png` : null;
  const fullImg = data?.main?.url ? mediaUrl(data.main.url) : null;
  const absImg = fullImg && fullImg.startsWith("/") ? `${BASE_URL}${fullImg}` : fullImg;
  const featureTitle = data ? `${appName} ${featureName} — ${isNl ? "screenshot & functie-overzicht" : "screenshot & feature overview"} | HorseAppCompare` : "HorseAppCompare";
  const featureDescription = data ? (isNl
    ? `Bekijk hoe ${appName} ${featureName.toLowerCase()} aanpakt: screenshot uit de live app, plus apps die hetzelfde aanbieden. Vergelijking door HorseAppCompare.`
    : `See how ${appName} handles ${featureName.toLowerCase()}: a screenshot pulled straight from the live product, plus other apps that ship the same feature. Review by HorseAppCompare.`) : "";

  useSocialMeta(data ? {
    title: featureTitle,
    description: featureDescription,
    image: ogImg,
    type: "article",
  } : { title: "HorseAppCompare", description: "" });

  useEffect(() => {
    if (status !== "ok" || !data) return;
    const url = `${BASE_URL}/apps/${slug}/feature/${label}`;

    // JSON-LD: WebPage + mainEntity ImageObject + BreadcrumbList
    setJsonLd("feature-jsonld", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": url,
          name: featureTitle,
          description: featureDescription,
          url,
          inLanguage: isNl ? "nl" : "en",
          isPartOf: { "@type": "WebSite", name: "HorseAppCompare", url: BASE_URL },
          mainEntity: {
            "@type": "ImageObject",
            contentUrl: absImg,
            name: `${appName} — ${featureName}`,
            caption: data.main.caption || `${appName} ${featureName}`,
            representativeOfPage: true,
            about: {
              "@type": "SoftwareApplication",
              name: appName,
              applicationCategory: "BusinessApplication",
              url: `${BASE_URL}/apps/${slug}`,
            },
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
            { "@type": "ListItem", position: 2, name: isNl ? "Directory" : "Directory", item: `${BASE_URL}/apps` },
            { "@type": "ListItem", position: 3, name: appName, item: `${BASE_URL}/apps/${slug}` },
            { "@type": "ListItem", position: 4, name: featureName, item: url },
          ],
        },
      ],
    });
  }, [status, data, slug, label, isNl, featureTitle, featureDescription, absImg, appName, featureName]);

  if (status === "notfound") return <Navigate to={`/apps/${slug}`} replace />;
  if (status === "loading" || !data) {
    return <main className="hac-container py-20 text-center text-[#94A3B8]">…</main>;
  }

  const { app, main, siblings, related_apps } = data;
  const featureLabel = main.feature_label;

  return (
    <main>
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-10 md:py-16 max-w-5xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6 flex-wrap" data-testid="feature-breadcrumb">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/apps" className="hover:text-[#2563EB]">{isNl ? "Directory" : "Directory"}</Link>
            <ChevronRight size={12} />
            <Link to={`/apps/${app.slug}`} className="hover:text-[#2563EB]">{app.name}</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{featureLabel}</span>
          </nav>

          <div className="overline mb-3 text-[#2563EB] inline-flex items-center gap-2">
            <Sparkles size={12} /> {isNl ? "Feature deep-dive" : "Feature deep-dive"}
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-[#0F172A] leading-[1.05] mb-5">
            {app.name} — {featureLabel}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">
            {isNl
              ? `Hoe ${app.name} ${featureLabel.toLowerCase()} aanpakt — screenshot uit de live app, plus alternatieven die dezelfde functie aanbieden.`
              : `How ${app.name} handles ${featureLabel.toLowerCase()} — screenshot from the live app, plus alternatives that ship the same feature.`}
          </p>
        </div>
      </section>

      {/* Hero image */}
      <section className="hac-container py-12 max-w-5xl">
        <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] bg-white" data-testid="feature-hero">
          <img
            src={mediaUrl(main.url)}
            alt={`${app.name} ${featureLabel}${main.caption ? ` — ${main.caption}` : ""}`}
            className="w-full max-h-[640px] object-contain bg-[#F8FAFC]"
          />
          {(main.caption || main.feature_label) && (
            <div className="p-5 border-t border-[#E2E8F0]">
              <div className="overline mb-2 text-[#2563EB]">{featureLabel}</div>
              <div className="text-[#0F172A] font-semibold">{main.caption || `${app.name} ${featureLabel}`}</div>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href={app.website ? goUrl(app.slug, "feature", { campaign: `feature-${label}` }) : `/apps/${app.slug}`}
            target={app.website ? "_blank" : undefined}
            rel={app.website ? "noopener sponsored nofollow noreferrer" : undefined}
            className="hac-btn-primary"
            data-testid="feature-cta-visit"
          >
            {isNl ? `Bekijk ${app.name}` : `Visit ${app.name}`} <ExternalLink size={14} />
          </a>
          <Link to={`/apps/${app.slug}`} className="hac-btn-secondary" data-testid="feature-cta-back">
            <ArrowLeft size={14} /> {isNl ? `Terug naar ${app.name}` : `Back to ${app.name}`}
          </Link>
          <Link to="/advisor" className="text-sm text-[#2563EB] hover:underline ml-auto">
            {isNl ? "Advisor →" : "Advisor →"}
          </Link>
        </div>
      </section>

      {/* Related apps with same feature */}
      {related_apps.length > 0 && (
        <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
          <div className="hac-container py-12 max-w-5xl">
            <div className="overline text-[#2563EB] mb-2">{isNl ? "Alternatieven" : "Alternatives"}</div>
            <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-6">
              {isNl
                ? `Andere apps met ${featureLabel.toLowerCase()}`
                : `Other apps that also ship ${featureLabel.toLowerCase()}`}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feature-related-apps">
              {related_apps.map((r) => (
                <Link
                  key={r.slug}
                  to={`/apps/${r.slug}/feature/${label}`}
                  className="bg-white border border-[#E2E8F0] hover:border-[#2563EB] rounded-xl p-4 flex items-center gap-3 transition-colors"
                  data-testid={`feature-related-${r.slug}`}
                >
                  <span
                    className="w-10 h-10 flex items-center justify-center text-white font-bold rounded-md text-sm"
                    style={{ backgroundColor: r.color || "#2563EB" }}
                  >
                    {r.name.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F172A]">{r.name}</div>
                    <div className="text-xs text-[#94A3B8] inline-flex items-center gap-1">
                      <Star size={11} className="fill-[#2563EB] text-[#2563EB]" />
                      {r.rating?.toFixed(1) || "—"}
                    </div>
                  </div>
                  <ArrowUpRight size={14} className="text-[#94A3B8]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sibling features of same app */}
      {siblings.length > 0 && (
        <section className="hac-container py-12 max-w-5xl">
          <div className="overline text-[#2563EB] mb-2">
            {isNl ? "Meer van " : "More from "}{app.name}
          </div>
          <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-6">
            {isNl ? "Andere features van" : "Other features of"} {app.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feature-siblings">
            {siblings.map((s) => (
              <Link
                key={s.label_slug}
                to={`/apps/${slug}/feature/${s.label_slug}`}
                className="bg-white border border-[#E2E8F0] hover:border-[#2563EB] rounded-xl overflow-hidden transition-colors group"
                data-testid={`feature-sibling-${s.label_slug}`}
              >
                <div className="aspect-[4/3] bg-[#F1F5F9] overflow-hidden">
                  <img src={mediaUrl(s.url)} alt={s.feature_label} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" loading="lazy" />
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#2563EB] mb-1">{s.feature_label}</div>
                  <div className="text-xs text-[#64748B]">{isNl ? "Bekijk feature →" : "View feature →"}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default AppFeaturePage;
